import React, { Suspense, lazy, useContext, useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { scheduleIdleTask, prefetchComponents, recordPerformanceMark } from './utils/performance';
import apiDebug from './utils/apiDebug';
import './App.css';
import DynamicRouter from './DynamicRouter';

// Core components that everyone needs
import NavBar from './components/NavBar';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';

// Public route components - eagerly loaded
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import TradingCardPage from './pages/TradingCardPage';
import UserProfilePage from './pages/UserProfilePage';
import TradingCardSetsPage from './pages/TradingCardSetsPage';
import ProductPage from './pages/ProductPage';
import InsertSetPage from './pages/InsertSetPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import TermsPage from './pages/TermsPage';

// Pages for authenticated users - lazy loaded by role
// User pages - separate chunk
const userPages = {
  EditProfilePage: lazy(() => import(/* webpackChunkName: "user" */ './pages/EditProfilePage'))
};

function App() {
  const { currentUser, loading } = useContext(AuthContext);
  const isAdmin = currentUser?.isAdmin === true;
  
  // Initialize API debugging and logging
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      apiDebug.setupAxiosDebug();
      console.log('🔍 API debugging initialized');
    }
  }, []);

  // Prefetch user pages if user is logged in
  useEffect(() => {
    if (currentUser && !loading) {
      recordPerformanceMark('prefetchUserPages', 'start');
      
      // Prefetch user pages when user is logged in and idle
      scheduleIdleTask(() => {
        prefetchComponents(userPages).then(() => {
          recordPerformanceMark('prefetchUserPages', 'end');
        });
      });
    }
  }, [currentUser, loading]);


  return (
    <ToastProvider>
      <div className="app">
        <ScrollToTop />
        <NavBar />
        <main className="main-content">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
            {/* Public Routes - Available to everyone */}
            <Route path="" element={<Home />} />
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route path="trading-card-sets" element={<TradingCardSetsPage />} />
            <Route path="trading-card-set/:productSlug" element={<ProductPage />} />
            <Route path="trading-card-set/:productName/:insertSetSlug" element={<InsertSetPage />} />
            
            {/* Card detail routes */}
            <Route path="trading-card-set/:productName/:insertSetName/:cardSlug" element={<TradingCardPage />} />
            <Route path="profile/:userId" element={<UserProfilePage />} />
            <Route path="privacy" element={<PrivacyPage />} />
            <Route path="cookie-policy" element={<CookiePolicyPage />} />
            <Route path="terms" element={<TermsPage />} />
            
            {/* Protected Routes - Only for logged-in users */}
            {currentUser && !loading && (
              <>
                <Route 
                  path="profile" 
                  element={
                    <ProtectedRoute>
                      <userPages.EditProfilePage />
                    </ProtectedRoute>
                  } 
                />
              </>
            )}
            
            
            {/* Catch-all route for protected pages when not logged in */}
            <Route 
              path="*" 
              element={loading ? <LoadingSpinner /> :  <DynamicRouter />}
            />
          </Routes>
        </Suspense>
      </main>
        <Footer />
      </div>
    </ToastProvider>
  );
}

export default App;